import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, ImageIcon, Trash2 } from "lucide-react";

/**
 * Carica un'immagine via POST /api/admin/upload-image e ritorna l'URL firmato.
 * Props:
 *   value: URL corrente (string)
 *   onChange: (url:string) => void
 *   category: 'routes' | 'rewards' | 'points' (per organizzare lo storage)
 *   testIdPrefix: prefisso per data-testid (default 'image')
 */
export default function ImageUploader({ value, onChange, category = "misc", testIdPrefix = "image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 25MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/upload-image?category=${encodeURIComponent(category)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.image_url);
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload immagine");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-uploader`}>
      <div className="flex items-stretch gap-2">
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... oppure carica un file"
          className="h-11 flex-1 px-3 rounded-2xl border border-outline bg-white focus:outline-none focus:ring-2 focus:ring-gold text-sm"
          data-testid={`${testIdPrefix}-url-input`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="h-11 px-4 rounded-2xl bg-ocean text-white font-bold flex items-center gap-2 disabled:opacity-50 shadow-tactile"
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          <Upload size={16}/> {busy ? "..." : "Carica"}
        </button>
        {value && (
          <button
            type="button"
            onClick={clear}
            className="h-11 px-3 rounded-2xl bg-white border border-outline text-compass hover:bg-red-50"
            title="Rimuovi immagine"
            data-testid={`${testIdPrefix}-clear-btn`}
          >
            <Trash2 size={16}/>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
      />
      {value ? (
        <div className="rounded-2xl overflow-hidden border border-outline bg-parchment-dark relative mx-auto max-w-[200px]" data-testid={`${testIdPrefix}-preview`}>
          <img
            src={value}
            alt="Anteprima"
            className="w-full"
            style={{ aspectRatio: "4 / 5", objectFit: "cover" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[9px] font-bold pointer-events-none">
            4:5
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-outline p-6 text-center text-ink-500 text-sm">
          <ImageIcon className="mx-auto mb-1" size={24}/>
          Nessuna immagine. Carica un file (JPG/PNG/WebP/HEIC, max 25MB) o incolla un URL.
        </div>
      )}
    </div>
  );
}
